/**
 * Client-side movement prediction for the local player (UO-style per-step).
 *
 * The walker wraps the *same* {@link Mobile} state machine the server ticks -
 * built from the owned entity's serialized {@link MobileState}, so walk/run/turn
 * timings match the authority exactly - and drives it from the live input at the
 * render cadence. The local player therefore moves the instant a key is held,
 * with no round trip. Each **turn start** and **step start** the mobile makes
 * emits exactly one sequence-numbered {@link WalkRequestMessage}; the server
 * acks each applied request ({@link onAck}) or denies it with an authoritative
 * pose ({@link onReject}), and the pending queue between the two is what bounds
 * the rubber-band: at most {@link MAX_PENDING} (5) unacked actions are ever in
 * flight, and while the queue is full (or the walker awaits a re-anchor after a
 * desync) input is refused - the character parks on its tile boundary.
 *
 * Deliberately pure: no DOM, no Pixi, no socket, and no wall clock - time
 * arrives as `dtMs` from the caller's ticker, messages leave as return values.
 * `main.ts` owns the wiring; this owns the protocol bookkeeping, so the whole
 * per-step client contract unit-tests in plain Node.
 */
import { Mobile, type IsWalkable, type MobileState, type TilePoint } from '../sim/mobile';
import type { Direction } from '../sim/direction';
import {
  DUNGEON_RECONCILIATION_TOLERANCE_TILES,
  dungeonCellFor,
  dungeonRushProgress,
  integrateDungeonMovement,
  type DungeonMovementIntent,
} from '../sim/dungeonMovement';
import type { World } from '../sim/world';
import type {
  DungeonMoveIntentMessage,
  WalkRequestMessage,
  MoveRejectMessage,
} from './protocol';

/**
 * The in-flight cap: how many unacked walk requests may be outstanding, matching
 * classic UO's 5-step queue and the server bank's {@link WALK_BANK_CAP_STEPS}.
 * Full queue => input refusal, which is also what bounds a mispredicted
 * rubber-band to at most 5 tiles.
 */
export const MAX_PENDING = 5;

/**
 * How many recently ack-confirmed tiles {@link PredictedWalker} remembers to
 * distinguish a stale, lagging position snapshot from real drift (ADR-0235). The
 * client's prediction leads the authority by at most {@link MAX_PENDING} unacked
 * steps, so a delta reflecting an already-acked move can lag the walker by at most
 * roughly that many tiles; a small margin over `MAX_PENDING` covers broadcast-cadence
 * jitter without ever masking a genuine divergence to a *novel* tile.
 */
export const CONFIRMED_HISTORY = MAX_PENDING + 3;

/**
 * How long (in walker time - accumulated `update` ms) a confirmed tile keeps
 * counting as plausible **stale lag** for {@link PredictedWalker.observeSnapshot}'s
 * stale-vs-drift test (ADR-0248). Stale in-flight deltas drain within roughly one
 * RTT plus a broadcast interval (~a few hundred ms), so 3s is generous; past it, a
 * snapshot resting on a recently-visited tile is genuine drift and must snap.
 * Without the bound, a real divergence onto a just-walked tile (exactly what a
 * missed server-side step produces) matched the history forever and the safety
 * net never fired - the desync persisted invisibly until some later reject
 * erupted as a multi-tile rubber-band.
 */
export const STALE_LAG_WINDOW_MS = 3000;

/**
 * Sub-update quantum. A single large `dtMs` (a janky frame, a tab returning)
 * could otherwise start more than one action inside one `mobile.update`, and the
 * walker must emit one request per action start. Advancing in chunks shorter
 * than the shortest possible action (the 45ms fast turn beat) guarantees at most
 * one action starts per chunk, so per-chunk state diffing detects every start.
 */
const CHUNK_MS = 20;

/** The desired movement input this frame, exactly the ticker's resolved intent. */
export interface DesiredMove {
  dir: Direction | null;
  running: boolean;
  turnOnly: boolean;
}

/** An authoritative-or-predicted resting pose (tile + level + facing). */
export interface WalkerPose {
  x: number;
  y: number;
  z: number;
  facing: Direction;
}

/** One unacked request: its seq and the predicted post-action pose. */
interface PendingMove {
  seq: number;
  x: number;
  y: number;
  facing: Direction;
}

interface PredictedRush {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  elapsedMs: number;
  durationMs: number;
}

interface PredictedDungeonMovement {
  seq: number;
  desired?: DesiredMove;
  intent: DungeonMovementIntent;
  elapsedMs: number;
  /**
   * The server's status-fold movement scalar (PRD #804). It scales distance
   * per millisecond on every integration here, never the elapsedMs ledger the
   * checkpoint protocol validates, exactly as the server applies it. Absent
   * means one.
   */
  speedScalar?: number;
}

export class PredictedWalker {
  private readonly mobile: Mobile;
  /** The walker's level; changes only via an authoritative pose (reject/snap). */
  private level: number;
  /** Unacked requests, oldest first; the head is what the next ack must name. */
  private readonly pending: PendingMove[] = [];
  /** The next seq to stamp: 1..255, wrapping to 1 (0 is never sent). */
  private nextSeq = 1;
  /** The epoch echoed on every request, adopted from the last reject. */
  private epoch = 0;
  /**
   * Set when an ack named something other than the queue head (a desync the
   * protocol cannot repair locally): input is refused and prediction frozen
   * until the next authoritative pose (a reject, or a snapshot) re-anchors.
   */
  private awaitingAnchor = false;
  /** The last pose the server confirmed (acked prediction, reject, or snap). */
  private authoritative: WalkerPose;
  /**
   * Recently ack-confirmed resting tiles, newest last (ADR-0235). The drift-snap in
   * {@link observeSnapshot} consults this to tell **stale lag** from **real drift**:
   * a `MoveAck` clears the pending head the instant the server *receives* a `Walk`,
   * but the position delta showing that new tile only rides the next broadcast, so
   * for ~RTT after the queue empties the client keeps receiving deltas/keyframes that
   * still show the tile it *just left*. Snapping to one of those yanked the local
   * player back a tile the moment they stopped - invisible on localhost (~0 RTT),
   * glaring under real latency. Because `empty pending` means the server has acked
   * every predicted move, any snapshot pose matching a recently-confirmed tile is
   * provably that stale lag, never an unexplained divergence (a genuine reposition
   * arrives as a {@link onReject}). Capped at {@link CONFIRMED_HISTORY} tiles - the
   * pipeline can't lag further behind than the prediction can lead - and each entry
   * expires {@link STALE_LAG_WINDOW_MS} of walker time after it was confirmed
   * (ADR-0248), so a *persistent* divergence onto a recently-visited tile is only
   * shielded for as long as stale lag can actually explain it.
   */
  private readonly confirmedHistory: (WalkerPose & { at: number })[] = [];
  /** Walker time: total `update` ms lived, the clock {@link confirmedHistory} entries age by. */
  private nowMs = 0;
  /**
   * Route-driven mode (ADR-0166): when set, the walker follows a whole planned
   * route via {@link Mobile.setPath}/`decidePathStep` instead of a per-frame held
   * direction, so it tracks the pathfinder's diagonal-then-straight line exactly
   * with no held-intent overshoot. While set, {@link advanceChunk} must **not**
   * call {@link Mobile.setIntent} (even `setIntent(null)` clears the route).
   */
  private pathMode = false;
  /** The route being followed while {@link pathMode}; the source for a paused re-apply. */
  private route: TilePoint[] = [];
  /**
   * Whether {@link route} is currently applied to the mobile. Cleared while input
   * is refused (the route is withheld so the mobile parks under the queue cap) and
   * restored when the queue drains - the route-mode mirror of the intent-mode gate.
   */
  private routeApplied = false;
  /** One local, server-mirrored dungeon Charge interpolation. */
  private rush?: PredictedRush;
  /** ARPG-only sub-tile offset layered over the Mobile collision anchor. */
  private positionOffset = { x: 0, y: 0 };
  /** Present only while the current private map is an active dungeon run. */
  private dungeon?: PredictedDungeonMovement;

  /**
   * Build the walker over the owned entity's movement config and resting pose.
   * Only the tile, facing, and timings are taken from the snapshot state - a
   * fresh walker always starts at rest (the spawned player is), never mid-step.
   */
  constructor(
    state: MobileState,
    z: number,
    position: { x: number; y: number } = state,
  ) {
    this.mobile = new Mobile({
      x: state.x,
      y: state.y,
      facing: state.dir,
      msPerTile: state.msPerTile,
      msPerTileRun: state.msPerTileRun,
      turnDelayMs: state.turnDelayMs,
      turnDelayFastMs: state.turnDelayFastMs,
    });
    this.positionOffset = {
      x: position.x - state.x,
      y: position.y - state.y,
    };
    this.level = z;
    this.authoritative = {
      x: position.x,
      y: position.y,
      z,
      facing: state.dir,
    };
    this.recordConfirmed(this.authoritative);
  }

  /**
   * Remember a freshly-confirmed resting pose for the {@link observeSnapshot}
   * stale-vs-drift test, capped at {@link CONFIRMED_HISTORY} (ADR-0235).
   */
  private recordConfirmed(pose: WalkerPose): void {
    this.confirmedHistory.push({ ...pose, at: this.nowMs });
    if (this.confirmedHistory.length > CONFIRMED_HISTORY) this.confirmedHistory.shift();
  }

  get tileX(): number {
    return this.mobile.tileX;
  }
  get tileY(): number {
    return this.mobile.tileY;
  }
  get facing(): Direction {
    return this.mobile.facing;
  }
  get isMoving(): boolean {
    const dungeon = this.dungeon;
    return (
      this.rush !== undefined ||
      (dungeon !== undefined && dungeon.intent.dir !== null) ||
      this.mobile.isMoving
    );
  }
  get isRunning(): boolean {
    const dungeon = this.dungeon;
    return (
      this.rush !== undefined ||
      (dungeon !== undefined &&
        dungeon.intent.dir !== null &&
        dungeon.intent.running) ||
      this.mobile.isRunning
    );
  }
  get isTurning(): boolean {
    return this.mobile.isTurning;
  }
  /** The walker's current level (z), for the render pose and the walkability predicate. */
  get z(): number {
    return this.level;
  }
  /** Unacked requests currently in flight (0..{@link MAX_PENDING}). */
  get pendingCount(): number {
    return this.pending.length;
  }
  /**
   * True while input is refused: the in-flight queue is full, or a desync froze
   * prediction until the next authoritative re-anchor.
   */
  get inputRefused(): boolean {
    return this.pending.length >= MAX_PENDING || this.awaitingAnchor;
  }

  /**
   * True while a moonwalk glide is playing locally (ADR-0145). The caller reads it to
   * refuse re-triggering a glide that is already in progress; `advanceChunk` reads the
   * underlying mobile flag to suppress per-step `Walk` requests for the scripted steps.
   */
  get isMoonwalking(): boolean {
    return this.mobile.isMoonwalking;
  }

  /** Fractional tile coordinates for rendering (the mobile's interpolation). */
  renderPosition(): { x: number; y: number } {
    if (this.rush) {
      const progress = Math.min(1, this.rush.elapsedMs / this.rush.durationMs);
      const eased = dungeonRushProgress(progress);
      return {
        x: this.rush.startX + (this.rush.endX - this.rush.startX) * eased,
        y: this.rush.startY + (this.rush.endY - this.rush.startY) * eased,
      };
    }
    const position = this.mobile.renderPosition();
    return {
      x: position.x + this.positionOffset.x,
      y: position.y + this.positionOffset.y,
    };
  }

  /** Exact resting coordinate used to originate ARPG actions. */
  simulationPosition(): { x: number; y: number } {
    return {
      x: this.mobile.tileX + this.positionOffset.x,
      y: this.mobile.tileY + this.positionOffset.y,
    };
  }

  /**
   * Position the server will have reached before a newly sent action command.
   * WebSocket ordering applies every already-sent Walk before that command even
   * while the local sprite is still interpolating toward the newest landing.
   */
  actionPosition(): { x: number; y: number } {
    if (this.dungeon) return this.simulationPosition();
    const latest = this.pending[this.pending.length - 1];
    return latest
      ? { x: latest.x, y: latest.y }
      : this.simulationPosition();
  }

  /**
   * Predict a server-authoritative dungeon Charge immediately.
   * The mobile rests at the shared planned endpoint while rendering eases there
   * over the short rush duration, and snapshot drift reconciliation waits for
   * that presentation to finish.
   */
  startDungeonRush(
    end: { x: number; y: number },
    facing: Direction,
    durationMs: number,
  ): void {
    const start = this.renderPosition();
    this.pathMode = false;
    this.route = [];
    this.routeApplied = false;
    const anchor = {
      x: Math.round(end.x),
      y: Math.round(end.y),
    };
    this.mobile.teleport(anchor.x, anchor.y);
    this.positionOffset = {
      x: end.x - anchor.x,
      y: end.y - anchor.y,
    };
    this.mobile.face(facing);
    this.rush = {
      startX: start.x,
      startY: start.y,
      endX: end.x,
      endY: end.y,
      elapsedMs: 0,
      durationMs: Math.max(1, durationMs),
    };
    if (this.dungeon) this.dungeon.elapsedMs = 0;
  }

  /**
   * Enter or leave the dungeon-only continuous prediction mode.
   * A supplied pose is the private-world spawn/exit anchor from the map swap.
   */
  setDungeonMode(active: boolean, pose?: WalkerPose): void {
    if (!active) {
      this.dungeon = undefined;
      this.rush = undefined;
      if (pose) this.snapTo(pose);
      return;
    }
    if (!this.dungeon) {
      this.pending.length = 0;
      this.pathMode = false;
      this.route = [];
      this.routeApplied = false;
      this.mobile.setIntent(null);
      this.mobile.setPath([]);
      this.dungeon = {
        seq: 0,
        intent: { dir: null, running: false },
        elapsedMs: 0,
      };
    }
    if (pose) this.snapTo(pose);
  }

  get isDungeonMovement(): boolean {
    return this.dungeon !== undefined;
  }

  /** Current ordered intent time for a dungeon action sent on the same socket. */
  dungeonMovementCheckpoint():
    | { seq: number; elapsedMs: number }
    | undefined {
    return this.dungeon && this.dungeon.seq > 0
      ? {
          seq: this.dungeon.seq,
          elapsedMs: this.dungeon.elapsedMs,
        }
      : undefined;
  }

  /**
   * Integrate dungeon locomotion immediately and emit only intent edges.
   * Charge owns the pose during its presentation, then any still-held intent
   * consumes the remainder of the frame and resumes from the exact endpoint.
   */
  updateDungeon(
    dtMs: number,
    desired: DesiredMove,
    world: World,
  ): DungeonMoveIntentMessage[] {
    this.nowMs += dtMs;
    if (!this.dungeon) this.setDungeonMode(true);
    const dungeon = this.dungeon!;
    const changed =
      dungeon.desired === undefined ||
      dungeon.desired.dir !== desired.dir ||
      dungeon.desired.running !== desired.running ||
      dungeon.desired.turnOnly !== desired.turnOnly;
    const messages: DungeonMoveIntentMessage[] = [];
    if (changed) {
      const previous =
        dungeon.seq > 0
          ? {
              seq: dungeon.seq,
              elapsedMs: dungeon.elapsedMs,
            }
          : undefined;
      dungeon.seq = dungeon.seq >= 0xffff ? 1 : dungeon.seq + 1;
      dungeon.desired = { ...desired };
      dungeon.intent = {
        dir: desired.turnOnly ? null : desired.dir,
        running: desired.running,
      };
      dungeon.elapsedMs = 0;
      if (desired.dir !== null) this.mobile.face(desired.dir);
      messages.push({
        kind: 'DungeonMoveIntent',
        seq: dungeon.seq,
        dir: desired.dir,
        running: desired.running,
        turnOnly: desired.turnOnly,
        ...(previous ? { previous } : {}),
      });
    }

    let movementMs = Math.max(0, dtMs);
    if (this.rush) {
      const remainingRushMs = Math.max(
        0,
        this.rush.durationMs - this.rush.elapsedMs,
      );
      const rushedMs = Math.min(movementMs, remainingRushMs);
      this.rush.elapsedMs += rushedMs;
      movementMs -= rushedMs;
      if (this.rush.elapsedMs >= this.rush.durationMs) this.rush = undefined;
    }
    if (movementMs > 0 && dungeon.intent.dir !== null) {
      const moved = integrateDungeonMovement(
        world,
        this.simulationPosition(),
        dungeon.intent,
        movementMs * (dungeon.speedScalar ?? 1),
        this.level,
      );
      this.setSimulationPosition(moved);
      if (moved.facing !== undefined) this.mobile.face(moved.facing);
      dungeon.elapsedMs += movementMs;
    }
    return messages;
  }

  /**
   * Reconcile a server movement acknowledgement by replaying the unacknowledged
   * duration of the current intent from its authoritative position.
   */
  observeDungeonMovement(
    authority: {
      seq: number;
      elapsedMs: number;
      x: number;
      y: number;
      facing: Direction;
      moveSpeedScalar?: number;
    },
    world: World,
  ): void {
    const dungeon = this.dungeon;
    if (!dungeon || this.rush || authority.seq !== dungeon.seq) return;
    // Adopt the authoritative fold scalar before reconciling, so a slow or a
    // Wraith Form reads as speed on the very update that announced it rather
    // than as a correction on the next one.
    dungeon.speedScalar = authority.moveSpeedScalar ?? 1;
    const current = this.simulationPosition();
    if (authority.elapsedMs > dungeon.elapsedMs) {
      const projectedCurrent = integrateDungeonMovement(
        world,
        current,
        dungeon.intent,
        (authority.elapsedMs - dungeon.elapsedMs) * dungeon.speedScalar,
        this.level,
      );
      if (
        Math.hypot(
          authority.x - projectedCurrent.x,
          authority.y - projectedCurrent.y,
        ) <= DUNGEON_RECONCILIATION_TOLERANCE_TILES
      ) {
        if (dungeon.intent.dir === null) this.mobile.face(authority.facing);
        return;
      }
      this.setSimulationPosition(authority);
      dungeon.elapsedMs = authority.elapsedMs;
      if (dungeon.intent.dir === null) this.mobile.face(authority.facing);
      return;
    }
    const replayMs = dungeon.elapsedMs - authority.elapsedMs;
    const replayed = integrateDungeonMovement(
      world,
      authority,
      dungeon.intent,
      replayMs * dungeon.speedScalar,
      this.level,
    );
    if (
      Math.hypot(replayed.x - current.x, replayed.y - current.y) >
      DUNGEON_RECONCILIATION_TOLERANCE_TILES
    ) {
      this.setSimulationPosition(replayed);
    }
    if (dungeon.intent.dir === null) this.mobile.face(authority.facing);
  }

  /**
   * Begin a **predicted** moonwalk glide (ADR-0145): the local player slides backward at
   * once, mirroring the authoritative glide the server runs from the single `Moonwalk`
   * command, so the moonwalker sees the walk clip and backward slide with no round trip -
   * exactly as normal walking is predicted. Deliberately emits **no** `Walk` requests: the
   * backward steps are server-driven (the reducer starts the same glide), so predicting
   * them locally must not also send per-step walk intents that would double-drive or fail
   * the forward-only walk gate (`advanceChunk` suppresses them). Refused while input is
   * frozen (queue full / awaiting a re-anchor) so a glide never layers over a desync.
   */
  startMoonwalk(): void {
    if (this.inputRefused) return;
    // A moonwalk is its own steering mode: drop any route so `advanceChunk` won't
    // re-apply it over the glide (ADR-0145/0166).
    this.pathMode = false;
    this.route = [];
    this.routeApplied = false;
    this.mobile.startMoonwalk();
  }

  /**
   * True while the walker is following a planned route (ADR-0166): tap-to-walk and
   * rat pursuit drive it this way. The caller reads it to keep the directional
   * cursor pointing along the walk while path-driven.
   */
  get isPathWalking(): boolean {
    return this.pathMode;
  }

  /**
   * Drive the local player along a planned **route** (ADR-0166): the walker hands
   * `tiles` to {@link Mobile.setPath}, which steps tile-to-tile via `decidePathStep`
   * and **parks exactly on the last tile** - so the walk follows the pathfinder's
   * diagonal-then-straight line with no held-intent overshoot and no zig-zag. The
   * tiles are copied. A re-path re-issues a fresh route the same way (the mobile
   * re-syncs onto it); an empty `tiles` stops. While path-driven, `update`'s held
   * direction is ignored (see {@link pathMode}) - manual steering must first
   * {@link clearPath} (main.ts routes that through `autowalk.cancel`). Per-step
   * `Walk` emission and the {@link inputRefused} queue-full gate are unchanged: one
   * request per step/turn start, and the route is withheld while the queue is full.
   */
  setPath(tiles: readonly TilePoint[]): void {
    this.route = tiles.map((p) => ({ x: p.x, y: p.y }));
    this.pathMode = this.route.length > 0;
    if (this.pathMode && !this.inputRefused) {
      this.mobile.setPath(this.route);
      this.routeApplied = true;
    } else {
      // Empty route stops now; a refused (re)start defers the apply to `advanceChunk`.
      this.mobile.setPath([]);
      this.routeApplied = false;
    }
  }

  /**
   * Leave route-driven mode and park on the next tile boundary (ADR-0166): manual
   * steering / typing / arrival routes through here (via `autowalk.cancel`) so the
   * next held direction drives the walker again. Idempotent.
   */
  clearPath(): void {
    if (!this.pathMode && this.route.length === 0) return;
    this.pathMode = false;
    this.route = [];
    this.routeApplied = false;
    this.mobile.setPath([]);
  }

  /**
   * Advance prediction by `dtMs` under the currently held input, returning the
   * walk requests (0..n) this advance started, in order - the caller sends them
   * up the socket verbatim. The walker pulls the held direction at the mobile's
   * own cadence (turn beat, step chaining), so callers just report what is held
   * each frame; requests are emitted only when an action actually starts.
   */
  update(dtMs: number, desired: DesiredMove, isWalkable: IsWalkable): WalkRequestMessage[] {
    this.nowMs += dtMs;
    if (this.rush) {
      this.rush.elapsedMs += dtMs;
      if (this.rush.elapsedMs >= this.rush.durationMs) this.rush = undefined;
      return [];
    }
    const out: WalkRequestMessage[] = [];
    let remaining = dtMs;
    do {
      const chunk = Math.min(remaining, CHUNK_MS);
      this.advanceChunk(chunk, desired, isWalkable, out);
      remaining -= chunk;
    } while (remaining > 0);
    return out;
  }

  /** One <= {@link CHUNK_MS} sub-update: at most one action can start inside it. */
  private advanceChunk(
    chunkMs: number,
    desired: DesiredMove,
    isWalkable: IsWalkable,
    out: WalkRequestMessage[],
  ): void {
    // Queue full or awaiting re-anchor: refuse input. The mobile finishes any
    // in-progress step and parks on the boundary; nothing new starts or emits.
    const refused = this.inputRefused;
    if (this.pathMode) {
      // Route-driven (ADR-0166): the mobile follows the whole route itself, so we
      // must NOT setIntent here - even a null intent clears the route
      // (`Mobile.setIntent`). Honour the input-refusal cap by withholding the route
      // while the queue is full (the mobile finishes its step and parks) and
      // restoring it when the queue drains, so at most MAX_PENDING steps are ever in
      // flight - exactly the intent-mode gate above.
      if (refused) {
        if (this.routeApplied) {
          this.mobile.setPath([]);
          this.routeApplied = false;
        }
      } else if (!this.routeApplied) {
        this.mobile.setPath(this.route);
        this.routeApplied = true;
      }
    } else {
      this.mobile.setIntent(refused ? null : desired.dir, desired.running, desired.turnOnly);
    }

    const facingBefore = this.mobile.facing;
    const movingBefore = this.mobile.isMoving;
    const targetBefore = this.mobile.targetTile();

    this.mobile.update(chunkMs, isWalkable);

    // A facing change is a turn start (Mobile only changes facing there). The
    // chunk bound means a step cannot also have started; emit and return.
    if (this.mobile.facing !== facingBefore) {
      out.push(this.emit(this.mobile.facing, false, false, {
        x: this.mobile.tileX + this.positionOffset.x,
        y: this.mobile.tileY + this.positionOffset.y,
        facing: this.mobile.facing,
      }));
      return;
    }

    // A step start: now moving toward a target it was not already heading for
    // (covers both rest -> step and a chained step completing + next starting).
    const target = this.mobile.targetTile();
    const stepStarted =
      this.mobile.isMoving &&
      (!movingBefore || target.x !== targetBefore.x || target.y !== targetBefore.y);
    // A moonwalk glide step is server-driven (the reducer runs the same glide from the one
    // `Moonwalk` command), so predict it locally for the smooth backward slide but emit
    // **no** `Walk` request for it: it steps opposite the facing and would fail the
    // forward-only walk gate, and double-driving it would desync (ADR-0145). Every ordinary
    // step still emits as before.
    if (stepStarted && !this.mobile.isMoonwalking) {
      out.push(this.emit(this.mobile.facing, this.mobile.isRunning, true, {
        x: target.x + this.positionOffset.x,
        y: target.y + this.positionOffset.y,
        facing: this.mobile.facing,
      }));
    }
  }

  /**
   * Stamp + queue one request for an action that just started. `step` is the
   * client's own turn-vs-step classification, carried on the wire (ADR-0248) so
   * the server applies exactly what the client performed instead of re-inferring
   * the kind from its own (possibly cosmetically diverged) facing.
   */
  private emit(
    dir: Direction,
    running: boolean,
    step: boolean,
    predicted: { x: number; y: number; facing: Direction },
  ): WalkRequestMessage {
    const seq = this.nextSeq;
    this.nextSeq = seq >= 255 ? 1 : seq + 1;
    this.pending.push({ seq, ...predicted });
    return { kind: 'Walk', seq, epoch: this.epoch, dir, running, step };
  }

  /**
   * The server applied a request. In-order protocol: the ack must name the
   * pending head, whose predicted pose is thereby confirmed authoritative. Any
   * other seq means the two sides disagree about the stream itself - clear the
   * queue, snap back to the last confirmed pose, and freeze input until the
   * next authoritative re-anchor (a reject, or the next snapshot).
   */
  onAck(seq: number): void {
    const head = this.pending[0];
    if (head && head.seq === seq) {
      this.pending.shift();
      this.authoritative = { x: head.x, y: head.y, z: this.level, facing: head.facing };
      this.recordConfirmed(this.authoritative);
      return;
    }
    console.warn(
      `[walker] unexpected MoveAck seq ${seq} (head ${head?.seq ?? 'none'}): freezing until re-anchor`,
    );
    this.pending.length = 0;
    this.snapTo(this.authoritative);
    this.awaitingAnchor = true;
  }

  /**
   * The server denied a request (`seq > 0`) or repositioned us outright
   * (`seq === 0`, a teleporter landing / resync): clear everything predicted,
   * snap to the carried pose (tile, level, facing), adopt the fresh epoch, and
   * restart the sequence at 1 - exactly what the server's gate now expects.
   */
  onReject(msg: MoveRejectMessage): void {
    this.pending.length = 0;
    this.snapTo({ x: msg.x, y: msg.y, z: msg.z, facing: msg.facing });
    this.epoch = msg.epoch;
    this.nextSeq = 1;
    this.awaitingAnchor = false;
  }

  /**
   * Reconcile against the own entity's pose from a freshly adopted snapshot -
   * the keyframe drift safety net. While requests are pending the snapshot
   * legitimately lags the prediction, so it is ignored; with an **empty** queue
   * the server and walker must agree on the resting tile, and a difference
   * means drift the ack stream cannot explain - snap to the server's truth.
   * Also the re-anchor a desync-frozen walker waits for.
   */
  observeSnapshot(pose: WalkerPose): void {
    if (this.dungeon) return;
    if (this.awaitingAnchor) {
      this.snapTo(pose);
      this.awaitingAnchor = false;
      return;
    }
    if (this.pending.length > 0) return;
    // A moonwalk glide is server-driven *and* client-predicted with **no** per-step
    // acks (it emits no `Walk`, so the queue stays empty) - and the local client
    // leads, predicting the whole backward slide before the authority has stepped
    // (ADR-0145). So during the glide every snapshot legitimately lags: the actor is
    // already tiles back while the snapshot still rests near the start. Reconciling
    // then would snap the actor *forward* toward the start (the "glide back, jump
    // forward, snap back" glitch). The drift-snap must stay out of the way for the
    // **whole** glide, not just while one step interpolates: between glide steps the
    // walker briefly parks at rest (`isMoving` false) with tiles still to slide, and a
    // snapshot arriving in that gap would otherwise yank it. `isMoonwalking` stays true
    // until the last tile lands, so the predicted glide plays out uninterrupted and the
    // safety net resumes cleanly only at the true end (the ADR's intended behaviour).
    if (
      this.rush ||
      this.mobile.isMoving ||
      this.mobile.isTurning ||
      this.mobile.isMoonwalking
    ) {
      return;
    }
    const current = this.simulationPosition();
    if (
      Math.abs(pose.x - current.x) > 0.0001 ||
      Math.abs(pose.y - current.y) > 0.0001 ||
      pose.z !== this.level
    ) {
      // Distinguish stale lag from real drift (ADR-0235). With the queue empty the
      // server has acked every predicted move, so a snapshot showing a tile we
      // recently confirmed-and-left is an in-flight delta still catching up (the ack
      // cleared `pending` ~RTT before this delta was sent), NOT an unexplained
      // divergence. Snapping to it would rubber-band the local player back a tile the
      // instant they stop - the launch-visible "jump back one tile" bug. Ignore it; the
      // next delta, showing the true resting tile, agrees and no snap is needed. Only a
      // pose we've *never* confirmed is genuine drift the safety net must correct.
      // The match is time-bounded (ADR-0248): stale lag drains within ~an RTT of
      // stopping, so a match older than STALE_LAG_WINDOW_MS of walker time is a real,
      // *persistent* divergence (the server resting on a tile we walked through) that
      // must snap - unbounded, the history shielded exactly that desync forever.
      const stale = this.confirmedHistory.some(
        (p) =>
          p.x === pose.x &&
          p.y === pose.y &&
          p.z === pose.z &&
          this.nowMs - p.at <= STALE_LAG_WINDOW_MS,
      );
      if (!stale) this.snapTo(pose);
    }
  }

  /**
   * Mirror the server's arena cast turn (ADR-0192): when a volley fires, the
   * authoritative run-world mobile snaps to face the primary projectile
   * (`sim/arena.volleyFacing`), and the owner derives the SAME direction from
   * the `ArenaFx` message and applies it here - no wire state, no `Walk`
   * emitted. Purely cosmetic since ADR-0248: every `Walk` carries the client's
   * own turn-vs-step classification, so a facing the mirror never applied (or
   * applied late) can no longer corrupt the gate - it only kept the shot clip
   * pointing the wrong way until the next walk overwrote it. (Pre-0248 the gate
   * inferred the kind from `Walk.dir === facing`, so a skipped mirror silently
   * downgraded the next step to a turn and stranded the authority a tile behind
   * - the cast-turn desync: routine for the fire mage's dodge-cast-dodge race,
   * every volley for the firing-while-moving archer.)
   *
   * Applies only while fully idle - anything else risks cancelling a live
   * route/intent/glide or corrupting an in-flight step prediction. A caster
   * moving when the ArenaFx lands simply skips the mirror.
   */
  faceCast(dir: Direction): void {
    if (this.pending.length > 0 || this.awaitingAnchor) return;
    if (this.rush) return;
    if (this.mobile.isMoving || this.mobile.isTurning || this.mobile.isMoonwalking) return;
    if (this.mobile.hasIntent || this.mobile.hasPath) return;
    this.mobile.face(dir);
    this.authoritative = { ...this.authoritative, facing: dir };
  }

  /** Adopt an authoritative pose wholesale: tile, level, and facing. */
  private snapTo(pose: WalkerPose): void {
    this.rush = undefined;
    const anchor = {
      x: Math.round(pose.x),
      y: Math.round(pose.y),
    };
    this.mobile.teleport(anchor.x, anchor.y);
    this.positionOffset = {
      x: pose.x - anchor.x,
      y: pose.y - anchor.y,
    };
    this.mobile.face(pose.facing);
    this.level = pose.z;
    this.authoritative = { ...pose };
    // An authoritative resync makes prior confirmed tiles irrelevant: the walker has
    // been repositioned, so start the stale-lag history over from the new truth (ADR-0235).
    this.confirmedHistory.length = 0;
    this.recordConfirmed(this.authoritative);
    if (this.dungeon) {
      this.dungeon.desired = undefined;
      this.dungeon.intent = { dir: null, running: false };
      this.dungeon.elapsedMs = 0;
    }
  }

  /** Replace only the precise dungeon coordinate and its derived Mobile anchor. */
  private setSimulationPosition(point: { x: number; y: number }): void {
    const anchor = dungeonCellFor(point);
    this.mobile.teleport(anchor.x, anchor.y);
    this.positionOffset = {
      x: point.x - anchor.x,
      y: point.y - anchor.y,
    };
  }
}
